import { Fragment, useCallback, useEffect, useRef, useState } from "react";
import { api, API, idr, pesanGalat } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import {
  Tags, Download, Warehouse, Building2, Eye, EyeOff, Boxes, Smartphone,
  ArrowUpDown, X, AlertTriangle, Info, ChevronLeft, ChevronRight, ShieldCheck, Pencil,
} from "lucide-react";
import LoadingState from "@/components/ui/loading-state";
import { useDebounce } from "@/hooks/useDebounce";
import { useDataQuery } from "@/hooks/useDataQuery";
import AksesModalPriceList from "../components/inventory/AksesModalPriceList";
import InfoUnitModal from "../components/inventory/InfoUnitModal";
import FilterProduk, {
  OPSI_QK, FILTER_PRODUK_KOSONG, ambilOpsiFilterProduk, daftarKategoriUtama, opsiKategoriRata,
} from "../components/inventory/FilterProduk";
import { formatTanggal } from "../lib/tanggal";

/**
 * Rangkuman Price List — brief owner 10 Sep 2026 poin 1, diperluas brief 22 Sep G.
 *
 * Semua penyaringan & pengurutan dikirim ke SERVER, tidak ada satu pun yang
 * dikerjakan atas array di layar: daftarnya dipotong per halaman, jadi
 * menyaring di sini akan membuat baris ke-101 tidak pernah ketemu sambil
 * melapor "tidak ada hasil" dengan yakin (jebakan yang sudah tercatat di
 * CLAUDE.md untuk filter piutang/utang).
 *
 * Baris filter = komponen yang SAMA dengan Master Produk (`FilterProduk`, G.1),
 * dan servernya memakai penyaring yang sama (`query_saring_produk`). Filter khas
 * Price List tetap di sini: cabang, "belum ada harga", rinci, Mode Daftar Harga.
 *
 * "Mode Daftar Harga" TIDAK menyembunyikan kolom dengan CSS — ia mengirim
 * `sembunyikan_modal=true` sehingga servernya yang tidak pernah mengirim angka
 * modal, dan file Excel-nya benar-benar tidak punya kolom itu. Menyembunyikan
 * di layar saja berarti file yang dikirim ke sales tetap membawa margin.
 *
 * Kolom modal dan margin tampil MASING-MASING menurut `modal_terlihat` /
 * `margin_terlihat` dari server (pengaturan "Akses Modal & Margin", brief owner
 * EppleHub 18 Sep 2026) — layar tidak pernah memutuskannya sendiri.
 *
 * "Tampilkan IMEI" (G.2) menumpuk unit ber-IMEI di bawah baris produknya —
 * server yang menyaring unit ke cakupan cabang pengguna dan tidak pernah
 * mengirim modal per unit. Tombol isi info unit mengikuti `bisa_isi_info_unit`
 * dari server (kunci `inventory_info_unit.update`, sama dengan endpoint-nya).
 */

const inp = "h-9 px-3 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-200 bg-white";
// Owner: "buat bisa sort by gudang, kategori, brand, cabang" — keempatnya ada
// di daftar ini, bukan cuma jadi filter.
const URUT_OPSI = [
  { v: "nama", l: "Nama" }, { v: "sku", l: "SKU" }, { v: "harga", l: "Harga jual" },
  { v: "stok", l: "Stok" }, { v: "margin", l: "Margin" },
  { v: "gudang", l: "Gudang" }, { v: "cabang", l: "Cabang" },
  { v: "kategori", l: "Kategori" }, { v: "brand", l: "Brand" },
];
const TESTID_FILTER = {
  cari: "pl-cari", kategori: "pl-kategori", subKategori: "pl-sub-kategori", brand: "pl-brand",
  gudang: "pl-gudang", stok: "pl-stok", supplier: "pl-supplier", gagal: "pl-filter-gagal",
  aging: "pl-aging", nonaktif: "pl-nonaktif",
};

function RentangHarga({ it }) {
  // Harga per unit (G1): tampil hanya bila ada unit berharga sendiri di cakupan.
  if (it.harga_min == null) return null;
  const sama = it.harga_min === it.harga_maks;
  return (
    <span className="block text-[11px] font-normal text-violet-700" data-testid={`pl-rentang-${it.sku}`}
          title="Harga per unit (IMEI) yang masih layak jual — unit tanpa harga sendiri memakai harga produk">
      unit: {sama ? idr(it.harga_min) : `${idr(it.harga_min)} – ${idr(it.harga_maks)}`}
    </span>
  );
}

function BarisImei({ units, kolom, bisaIsi, onIsi }) {
  return (
    <tr className="bg-violet-50/30">
      <td colSpan={kolom} className="px-3 pb-3 pt-1">
        <div className="overflow-x-auto rounded-lg border border-violet-100 bg-white ml-4 w-fit max-w-full">
          {/* Lebar tabel mengikuti isinya (bukan w-full): tabel induk lebih lebar dari
              layar (gudang/cabang/modal), jadi kolom yang direntang ke ujung kanan
              hanya terlihat setelah digeser. Aksi & harga unit sengaja di depan —
              dua hal yang paling dicari; yang tak terlihat sama dengan tidak ada. */}
          <table className="text-xs" data-testid="pl-imei-tabel">
            <thead className="bg-violet-50 text-violet-800">
              <tr>
                {bisaIsi && <th className="px-2 py-1.5 text-left font-semibold">Aksi</th>}
                <th className="px-2 py-1.5 text-left font-semibold">IMEI / SN</th>
                <th className="px-2 py-1.5 text-right font-semibold">Harga unit</th>
                <th className="px-2 py-1.5 text-right font-semibold">Battery</th>
                <th className="px-2 py-1.5 text-left font-semibold">Garansi</th>
                <th className="px-2 py-1.5 text-left font-semibold">Serial Number</th>
                <th className="px-2 py-1.5 text-left font-semibold">Gudang</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-violet-50">
              {units.map(u => (
                <tr key={u.id} data-testid={`pl-imei-${u.identifier}`}>
                  {bisaIsi && (
                    <td className="px-2 py-1.5">
                      <button type="button" onClick={() => onIsi(u)} data-testid={`pl-imei-isi-${u.identifier}`}
                              className="h-7 px-2 rounded border border-slate-200 text-[11px] text-slate-600 hover:bg-slate-50 inline-flex items-center gap-1 whitespace-nowrap">
                        <Pencil className="w-3 h-3" /> Isi info
                      </button>
                    </td>
                  )}
                  <td className="px-2 py-1.5 font-mono text-slate-800 whitespace-nowrap">
                    {u.identifier}
                    {u.status === "rusak" && (
                      <span className="ml-1.5 text-[10px] px-1 rounded bg-rose-50 text-rose-600 border border-rose-200">rusak</span>
                    )}
                  </td>
                  <td className="px-2 py-1.5 text-right whitespace-nowrap font-semibold text-slate-800">
                    {idr(u.harga || 0)}
                    {u.harga_jual_unit != null && <span className="block text-[10px] font-normal text-violet-600">harga unit</span>}
                  </td>
                  <td className={`px-2 py-1.5 text-right whitespace-nowrap ${
                    u.battery_health != null && u.battery_health < 80 ? "text-amber-600 font-semibold" : "text-slate-800"}`}>
                    {u.battery_health != null ? `${u.battery_health}%` : "—"}
                  </td>
                  <td className="px-2 py-1.5 text-slate-600 whitespace-nowrap">
                    {u.garansi_sampai ? (
                      <>
                        s/d {formatTanggal(u.garansi_sampai)}
                        {u.garansi_habis && (
                          <span className="ml-1 text-[10px] px-1 rounded bg-slate-100 text-slate-500 border border-slate-200">habis</span>
                        )}
                      </>
                    ) : "—"}
                    {u.garansi_keterangan && <span className="block text-[10px] text-slate-400">{u.garansi_keterangan}</span>}
                  </td>
                  <td className="px-2 py-1.5 font-mono text-slate-600 whitespace-nowrap">{u.serial_number || "—"}</td>
                  <td className="px-2 py-1.5 text-slate-600 whitespace-nowrap">{u.gudang || "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </td>
    </tr>
  );
}

export default function PriceList() {
  const { can } = useAuth();
  const bolehGudang = can("inventory_gudang", "view");

  const [rinci, setRinci] = useState("sku");
  // Pencarian: pola Master Produk — dikirim sesudah 400 ms diam, Enter langsung.
  const [qInput, setQInput] = useState("");
  const [q, setQ] = useState("");
  const qDebounced = useDebounce(qInput, 400);
  useEffect(() => { setQ(qDebounced); }, [qDebounced]);
  const [filter, setFilter] = useState(FILTER_PRODUK_KOSONG);
  const [cabang, setCabang] = useState("");
  const [tanpaHarga, setTanpaHarga] = useState(false);
  const [tampilImei, setTampilImei] = useState(false);
  const [modeDaftarHarga, setModeDaftarHarga] = useState(false);
  const [urut, setUrut] = useState("nama");
  const [arah, setArah] = useState("asc");
  const [page, setPage] = useState(1);

  const [cabangOpsi, setCabangOpsi] = useState([]);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [unduh, setUnduh] = useState(false);
  const [aturAkses, setAturAkses] = useState(false);
  const [isiUnit, setIsiUnit] = useState(null);   // { unit, produk } | null

  // Opsi filter + gudang + supplier: pengambil & cache yang SAMA dengan Master
  // Produk. Gudang hanya diminta bila `inventory_gudang.view` dipegang — tanpa
  // itu satu 403 tidak boleh mengosongkan halaman.
  const { data: opsiData } = useDataQuery(OPSI_QK, () => ambilOpsiFilterProduk(bolehGudang));
  const opsiMaster = opsiData?.opsi;
  const kategoriLabel = Object.fromEntries(
    opsiKategoriRata(daftarKategoriUtama(opsiMaster)).map(k => [k.v, k.l]));

  // Cabang: SATU sumber untuk semua dropdown cabang di aplikasi ini
  // (`/inventory/cabang-options`), gerbangnya cukup login.
  useEffect(() => {
    api.get("/inventory/cabang-options").then(r => setCabangOpsi(r.data.items || [])).catch(() => {});
  }, []);

  const paramFilter = useCallback(() => ({
    rinci, urut, arah,
    // "Belum ada harga" adalah status khas Price List; "Termasuk nonaktif"
    // milik baris filter bersama — server memetakan keduanya (price_list docstring).
    status: tanpaHarga ? "tanpa_harga" : "aktif",
    include_inactive: !!filter.include_inactive,
    ...(q.trim() ? { q: q.trim() } : {}),
    ...(filter.kategori ? { kategori: filter.kategori } : {}),
    ...(filter.sub_kategori ? { sub_kategori: filter.sub_kategori } : {}),
    ...(filter.brand ? { brand: filter.brand } : {}),
    ...(filter.supplier ? { supplier: filter.supplier } : {}),
    ...(filter.stok ? { stok: filter.stok } : {}),
    ...(filter.only_aging ? { only_aging: true } : {}),
    ...(cabang ? { cabang } : {}),
    ...(filter.warehouse_id ? { warehouse_id: filter.warehouse_id } : {}),
    ...(modeDaftarHarga ? { sembunyikan_modal: true } : {}),
    ...(tampilImei ? { tampil_imei: true } : {}),
  }), [rinci, urut, arah, tanpaHarga, filter, q, cabang, modeDaftarHarga, tampilImei]);

  const seq = useRef(0);
  const muat = useCallback(async () => {
    const n = ++seq.current;
    setLoading(true);
    try {
      const r = await api.get("/inventory/price-list", { params: { ...paramFilter(), page, per_page: 100 } });
      // Balasan yang datang terlambat dari filter LAMA tidak boleh menimpa
      // hasil filter yang sedang aktif.
      if (n === seq.current) setData(r.data);
    } catch (e) {
      if (n === seq.current) toast.error(pesanGalat(e, "Gagal memuat price list"));
    } finally {
      if (n === seq.current) setLoading(false);
    }
  }, [paramFilter, page]);

  useEffect(() => { muat(); }, [muat]);
  // Setiap perubahan filter mengembalikan ke halaman 1 — kalau tidak, memilih
  // filter baru saat berada di halaman 7 memberi layar kosong yang terlihat
  // seperti "tidak ada hasil".
  useEffect(() => { setPage(1); }, [rinci, q, filter, cabang, tanpaHarga]);

  const ubahFilter = (patch) => setFilter(f => ({ ...f, ...patch }));
  const bersihkan = () => {
    setQInput(""); setQ(""); setFilter(FILTER_PRODUK_KOSONG); setCabang(""); setTanpaHarga(false);
  };

  const unduhExcel = async () => {
    setUnduh(true);
    try {
      const p = new URLSearchParams(Object.entries(paramFilter()).map(([k, v]) => [k, String(v)]));
      const token = localStorage.getItem("hris_token");
      const res = await fetch(`${API}/inventory/price-list/export-xlsx?${p}`,
        { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error("gagal");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `Price_List_${cabang || "semua-cabang"}.xlsx`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success("Price list terunduh");
    } catch {
      toast.error("Gagal mengunduh Excel");
    } finally {
      setUnduh(false);
    }
  };

  const modalTerlihat = !!data?.modal_terlihat;
  const marginTerlihat = !!data?.margin_terlihat;
  // Tombol pengaturan mengikuti keputusan SERVER (superadmin/admin), sama dengan
  // gerbang endpoint-nya — bukan menebak dari role di layar (aturan #3).
  const bisaAturAkses = !!data?.bisa_atur_akses;
  // Tombol "Isi info" unit: keputusan server (`bisa_isi_info_unit`) DAN `can()`
  // dengan kunci & aksi yang sama dengan `PATCH /inventory/units/{id}/info`
  // (`inventory_info_unit.update`) — kalau salah satunya bilang tidak, tombol
  // tidak tampil; tidak ada jalan tombol tampil lalu berujung 403 (aturan #3).
  const bisaIsiInfo = !!data?.bisa_isi_info_unit && can("inventory_info_unit", "update");
  // Yang menentukan opsi urut "margin" adalah AKSES margin (`margin_diizinkan`),
  // bukan kolom yang sedang tampil: di Mode Daftar Harga margin tertutup
  // SEMENTARA atas pilihan pengguna — opsinya tetap ada supaya urutan margin
  // utuh lagi begitu mode dimatikan (server sendiri mengurutkan menurut nama
  // selama mode aktif). `margin_diizinkan` tidak bergantung mode, jadi balasan
  // basi dari saat mode masih aktif tidak bisa membuang pilihan itu.
  const marginDiizinkan = data ? data.margin_diizinkan !== false : true;
  const urutOpsi = URUT_OPSI.filter(o => o.v !== "margin" || marginDiizinkan);
  // Pilihan "margin" yang tersisa dari sebelum AKSES berubah (mis. owner baru
  // saja mematikan kolom margin) dikembalikan ke nama, supaya select tidak
  // menunjuk opsi yang sudah tidak ada.
  useEffect(() => {
    if (data && data.margin_diizinkan === false && urut === "margin") setUrut("nama");
  }, [data, urut]);
  const items = data?.items || [];
  const jumlahKolom = 8 + (modalTerlihat ? 1 : 0) + (marginTerlihat ? 1 : 0);

  const gantiUrut = (kol) => {
    if (urut === kol) setArah(arah === "asc" ? "desc" : "asc");
    else { setUrut(kol); setArah(kol === "nama" || kol === "sku" ? "asc" : "desc"); }
  };
  const Th = ({ kol, children, kanan }) => (
    <th className={`px-3 py-2 font-semibold whitespace-nowrap ${kanan ? "text-right" : "text-left"}`}>
      <button type="button" onClick={() => gantiUrut(kol)}
        data-testid={`pl-urut-${kol}`}
        className={`inline-flex items-center gap-1 hover:text-emerald-700 ${urut === kol ? "text-emerald-700" : ""}`}>
        {children}
        <ArrowUpDown className="w-3 h-3 opacity-60" />
      </button>
    </th>
  );

  return (
    <div className="p-4 md:p-6 space-y-4" data-testid="halaman-price-list">
      {/* Kepala */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-emerald-50 border border-emerald-100 grid place-items-center">
            <Tags className="w-5 h-5 text-emerald-600" />
          </div>
          <div>
            <h1 className="text-xl font-bold text-slate-900">Rangkuman Price List</h1>
            <p className="text-sm text-slate-500">
              Harga jual seluruh katalog, disandingkan dengan posisi stok.
            </p>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {bisaAturAkses && (
            <button type="button" onClick={() => setAturAkses(true)}
              data-testid="pl-akses-buka"
              title="Atur apakah modal & margin tampil, dan siapa saja yang boleh melihatnya"
              className="h-10 px-3 rounded-lg text-sm font-medium border bg-white border-slate-200 text-slate-600 hover:bg-slate-50 inline-flex items-center gap-2">
              <ShieldCheck className="w-4 h-4" />
              Akses Modal & Margin
            </button>
          )}
          {/* Mode Daftar Harga hanya bermakna bagi yang memang melihat modal
              atau margin; tetap tampil selama sedang aktif supaya bisa dimatikan. */}
          {(modalTerlihat || marginTerlihat || modeDaftarHarga) && (
          <button type="button" onClick={() => setModeDaftarHarga(v => !v)}
            data-testid="pl-mode-daftar-harga"
            title="Sembunyikan modal & margin, termasuk dari file Excel — aman dikirim ke sales"
            className={`h-10 px-3 rounded-lg text-sm font-medium border inline-flex items-center gap-2 ${
              modeDaftarHarga ? "bg-amber-50 border-amber-200 text-amber-700"
                              : "bg-white border-slate-200 text-slate-600 hover:bg-slate-50"}`}>
            {modeDaftarHarga ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
            Mode Daftar Harga
          </button>
          )}
          <button type="button" onClick={unduhExcel} disabled={unduh}
            data-testid="pl-export-xlsx"
            title={tampilImei ? "Excel berisi sheet kedua: satu baris per IMEI" : undefined}
            className="h-10 px-4 rounded-lg text-sm font-semibold bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-60 inline-flex items-center gap-2">
            <Download className="w-4 h-4" />
            {unduh ? "Menyiapkan…" : "Export Excel"}
          </button>
        </div>
      </div>

      {/* Filter — baris pertama SAMA dengan Master Produk (G.1) + filter khas Price List. */}
      <div className="bg-white border border-slate-200 rounded-xl p-3 space-y-3">
        <div className="flex flex-wrap items-center gap-2">
          <FilterProduk
            q={qInput} onQ={setQInput} onQEnter={() => setQ(qInput)}
            nilai={filter} onUbah={ubahFilter} opsi={opsiMaster}
            gudang={opsiData?.gudang || []} suppliers={opsiData?.suppliers || []}
            gudangGagal={!!opsiData?.gudangGagal} supplierGagal={!!opsiData?.supplierGagal}
            agingHari={opsiMaster?.aging_hari} testId={TESTID_FILTER}
            keteranganStok="Dibandingkan dengan stok di cabang/gudang yang sedang dipilih">
            <select value={cabang} onChange={e => setCabang(e.target.value)}
              data-testid="pl-cabang" className={inp} aria-label="Filter cabang">
              <option value="">Semua cabang</option>
              {cabangOpsi.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
            <button type="button" onClick={() => setTanpaHarga(v => !v)} data-testid="pl-tanpa-harga"
              title="Hanya produk yang harga jualnya belum diisi"
              className={`h-9 px-3 rounded-lg border text-xs font-medium inline-flex items-center gap-1.5 ${
                tanpaHarga ? "border-amber-300 bg-amber-50 text-amber-700" : "border-slate-200 bg-white text-slate-600 hover:bg-slate-50"}`}>
              <AlertTriangle className="w-3.5 h-3.5" /> Belum ada harga
            </button>
          </FilterProduk>
        </div>

        <div className="flex flex-wrap items-center gap-2 text-sm">
          <div className="inline-flex rounded-lg border border-slate-200 overflow-hidden">
            {[{ v: "sku", l: "Per barang", i: Tags }, { v: "gudang", l: "Rinci per gudang", i: Warehouse }].map(o => (
              <button key={o.v} type="button" onClick={() => setRinci(o.v)}
                data-testid={`pl-rinci-${o.v}`}
                className={`px-3 h-9 inline-flex items-center gap-2 ${
                  rinci === o.v ? "bg-emerald-600 text-white" : "bg-white text-slate-600 hover:bg-slate-50"}`}>
                <o.i className="w-4 h-4" />{o.l}
              </button>
            ))}
          </div>
          <button type="button" onClick={() => setTampilImei(v => !v)} data-testid="pl-tampil-imei"
            title="Tumpuk IMEI/SN, Battery Health, garansi, dan harga per unit di bawah tiap produk"
            className={`h-9 px-3 rounded-lg border text-sm inline-flex items-center gap-2 ${
              tampilImei ? "border-violet-300 bg-violet-50 text-violet-700" : "border-slate-200 bg-white text-slate-600 hover:bg-slate-50"}`}>
            <Smartphone className="w-4 h-4" /> Rincian IMEI
          </button>
          <select value={urut} onChange={e => setUrut(e.target.value)}
            data-testid="pl-urut" className={inp}>
            {urutOpsi.map(o => <option key={o.v} value={o.v}>Urut: {o.l}</option>)}
          </select>
          <button type="button" onClick={() => setArah(arah === "asc" ? "desc" : "asc")}
            data-testid="pl-arah" className={`${inp} hover:bg-slate-50`}>
            {arah === "asc" ? "A → Z / kecil → besar" : "Z → A / besar → kecil"}
          </button>
          <button type="button" onClick={bersihkan} data-testid="pl-bersihkan"
            className="h-9 px-3 rounded-lg border border-slate-200 text-slate-500 hover:bg-slate-50 inline-flex items-center gap-1">
            <X className="w-3.5 h-3.5" /> Bersihkan filter
          </button>
        </div>
      </div>

      {/* Catatan jujur dari server (mis. gudang serba-cabang, modal disembunyikan) */}
      {(data?.catatan || []).map((c, i) => (
        <div key={i} data-testid="pl-catatan"
          className="flex items-start gap-2 text-sm text-slate-600 bg-slate-50 border border-slate-200 rounded-lg px-3 py-2">
          <Info className="w-4 h-4 mt-0.5 text-slate-400 shrink-0" />
          <span>{c}</span>
        </div>
      ))}

      {/* Tabel */}
      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
        {loading ? (
          <LoadingState />
        ) : items.length === 0 ? (
          <div className="p-10 text-center text-slate-500 text-sm" data-testid="pl-kosong">
            <Boxes className="w-8 h-8 mx-auto mb-2 text-slate-300" />
            Tidak ada barang yang cocok dengan filter ini.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="pl-tabel">
              <thead className="bg-slate-50 text-slate-600 border-b border-slate-200">
                <tr>
                  <Th kol="sku">SKU</Th>
                  <Th kol="nama">Nama Barang</Th>
                  <Th kol="kategori">Kategori</Th>
                  <Th kol="brand">Brand</Th>
                  <Th kol="harga" kanan>Harga Jual</Th>
                  <Th kol="stok" kanan>Stok</Th>
                  <Th kol="gudang">Gudang</Th>
                  <Th kol="cabang">Cabang</Th>
                  {modalTerlihat && <th className="px-3 py-2 text-right font-semibold whitespace-nowrap">Modal/Unit</th>}
                  {marginTerlihat && <Th kol="margin" kanan>Margin</Th>}
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {items.map((it, i) => (
                  <Fragment key={`${it.sku}-${it.warehouse_id || "all"}-${i}`}>
                  <tr data-testid={`pl-baris-${it.sku}`} className="hover:bg-slate-50/60">
                    <td className="px-3 py-2 font-mono text-xs text-slate-500 whitespace-nowrap">{it.sku}</td>
                    <td className="px-3 py-2">
                      <div className="font-medium text-slate-800">
                        {it.nama}
                        {/* Produk paket (resep bundling aktif, brief 22 Sep F) —
                            stoknya turunan dari komponen, bukan kartu stok sendiri. */}
                        {it.paket && (
                          <span className="ml-1.5 align-middle text-[10px] px-1.5 py-0.5 rounded bg-violet-50 text-violet-700 border border-violet-200"
                            data-testid={`pl-paket-${it.sku}`}
                            title={"Paket: " + (it.paket_komponen || []).map(k => `${k.qty}× ${k.nama || k.sku}`).join(", ")}>
                            Paket
                          </span>
                        )}
                      </div>
                      {!it.aktif && <span className="text-[11px] text-slate-400">Nonaktif</span>}
                      {data?.tampil_imei && (it.units || []).length > 0 && (
                        <span className="text-[11px] text-violet-600">{it.units.length} IMEI di bawah</span>
                      )}
                    </td>
                    <td className="px-3 py-2 text-slate-600 whitespace-nowrap">
                      {kategoriLabel[it.kategori] || it.kategori || "—"}
                      {it.sub_kategori && <span className="text-slate-400"> / {it.sub_kategori}</span>}
                    </td>
                    <td className="px-3 py-2 text-slate-600 whitespace-nowrap">{it.brand || "—"}</td>
                    <td className="px-3 py-2 text-right font-semibold whitespace-nowrap">
                      {it.harga_jual > 0 ? idr(it.harga_jual) : (
                        <span className="text-amber-600 inline-flex items-center gap-1">
                          <AlertTriangle className="w-3.5 h-3.5" />belum diisi
                        </span>
                      )}
                      <RentangHarga it={it} />
                    </td>
                    <td className="px-3 py-2 text-right whitespace-nowrap">
                      <span className={it.stok > 0 ? "text-slate-800" : "text-slate-400"}
                        title={it.paket ? "Stok paket = berapa paket yang bisa dibentuk dari stok komponennya" : undefined}>
                        {it.stok}
                      </span>
                      {it.stok_rusak > 0 && (
                        <span className="text-[11px] text-rose-500 ml-1" title="unit rusak, tidak layak jual">
                          ({it.stok_rusak} rusak)
                        </span>
                      )}
                    </td>
                    <td className="px-3 py-2 text-slate-600 max-w-[220px] truncate" title={
                      (it.gudang_daftar || []).join(", ") || it.gudang || ""}>
                      {it.gudang || "—"}
                    </td>
                    <td className="px-3 py-2 text-slate-600 whitespace-nowrap">
                      {it.cabang || "—"}
                      {it.cabang_via && (
                        <span className="ml-1 text-[11px] px-1.5 py-0.5 rounded bg-sky-50 text-sky-700 border border-sky-100"
                          title="Sebagian stok berada di gudang yang melayani semua cabang">
                          <Building2 className="w-3 h-3 inline mr-0.5" />{it.cabang_via}
                        </span>
                      )}
                    </td>
                    {modalTerlihat && (
                      <td className="px-3 py-2 text-right whitespace-nowrap">
                        {it.modal == null ? <span className="text-slate-400">—</span> : (
                          <>
                            {idr(it.modal)}
                            {it.modal_sumber === "perkiraan" && (
                              <span className="block text-[11px] text-amber-600">perkiraan</span>
                            )}
                          </>
                        )}
                      </td>
                    )}
                    {marginTerlihat && (
                      <td className="px-3 py-2 text-right whitespace-nowrap">
                        {it.margin_persen == null ? (
                          <span className="text-slate-400" title="modal belum diketahui — bukan berarti untung">—</span>
                        ) : (
                          <span className={it.rugi ? "text-rose-600 font-semibold" : "text-slate-800"}>
                            {it.margin_persen.toFixed(1)}%
                          </span>
                        )}
                      </td>
                    )}
                  </tr>
                  {data?.tampil_imei && (it.units || []).length > 0 && (
                    <BarisImei units={it.units} kolom={jumlahKolom} bisaIsi={bisaIsiInfo}
                               onIsi={(u) => setIsiUnit({ unit: u, produk: it })} />
                  )}
                  </Fragment>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Paginasi — angka totalnya dari server, bukan dihitung dari baris yang
          sedang tampil (baris yang tampil sudah dipotong halaman). */}
      {data && data.total > 0 && (
        <div className="flex flex-wrap items-center justify-between gap-2 text-sm text-slate-600">
          <div data-testid="pl-ringkasan">
            Menampilkan <b>{items.length}</b> dari <b>{data.total}</b> barang
            {data.rinci === "gudang" && " (dirinci per gudang)"}
            {data.tanpa_harga_di_halaman > 0 && (
              <span className="text-amber-600"> · {data.tanpa_harga_di_halaman} belum ada harga jual</span>
            )}
          </div>
          <div className="flex items-center gap-1">
            <button type="button" disabled={page <= 1} onClick={() => setPage(p => p - 1)}
              data-testid="pl-prev"
              className="h-9 px-2 rounded-lg border border-slate-200 disabled:opacity-40 hover:bg-slate-50">
              <ChevronLeft className="w-4 h-4" />
            </button>
            <span className="px-2">Hal {data.page} / {data.total_pages}</span>
            <button type="button" disabled={page >= data.total_pages} onClick={() => setPage(p => p + 1)}
              data-testid="pl-next"
              className="h-9 px-2 rounded-lg border border-slate-200 disabled:opacity-40 hover:bg-slate-50">
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}
      {aturAkses && (
        <AksesModalPriceList onClose={() => setAturAkses(false)} onTersimpan={muat} />
      )}
      {isiUnit && bisaIsiInfo && (
        <InfoUnitModal unit={isiUnit.unit} produk={isiUnit.produk}
                       onClose={() => setIsiUnit(null)} onTersimpan={muat} />
      )}
    </div>
  );
}
